/**
 * Scene Timeline — corkboard (script order) + chronological swimlanes (story order).
 *
 * Script order: cards in screenplay order; reordering moves the real scene block
 * (saved via the editor's conflict-safe sync). A ghost slot shows exactly where a
 * dragged card will land, and the whole card is a drop target.
 *
 * Story order: a true swimlane grid. The horizontal axis is TIME (columns); each
 * row is a lane. Two scenes in DIFFERENT lanes can occupy the SAME column = they
 * happen at the same time. Lane rows grow to fit their cards (no overflow). Drag
 * a card left/right to set its time, up/down (drop on a lane) to set its lane;
 * dropping on a cell already taken in that lane auto-nudges to the next free one.
 * Story order never changes the script.
 *
 * Cards are resizable (width + height sliders, saved to the account) and shrink
 * smartly: drop the synopsis, then the character tags, then the INT/EXT prefix,
 * with text scaling down to a 9px floor.
 *
 * Brand: never says "AI".
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { X, Maximize2, Minimize2, ChevronLeft, ChevronRight, Plus, Trash2, Rows3, MoveHorizontal, MoveVertical, BookOpen, GripVertical, Undo2, Redo2 } from "lucide-react";

const GAP = 14;
const W_MIN = 96, W_MAX = 300, H_MIN = 70, H_MAX = 260;
const LANE_PALETTE = ["#8a5a44", "#3b6ea5", "#5b8a72", "#9b6a9e", "#b08534", "#a14d52", "#5f6caf", "#4f7a8a"];
const genId = () => Math.random().toString(36).slice(2, 9);

function splitBlocks(elements) {
  const preamble = [];
  const blocks = [];
  let cur = null;
  for (const el of elements || []) {
    if (el.type === "scene_heading") {
      if (cur) blocks.push(cur);
      cur = { sceneId: el.id, els: [el] };
    } else if (cur) { cur.els.push(el); } else { preamble.push(el); }
  }
  if (cur) blocks.push(cur);
  return { preamble, blocks };
}
function flatten(preamble, blocks) {
  return [...preamble, ...blocks.flatMap((b) => b.els)];
}

// Strip a leading INT./EXT. (and variants) so a tiny card can show just the location.
function compactHeading(heading) {
  return (heading || "").replace(/^\s*(INT\.?\/EXT\.?|EXT\.?\/INT\.?|INT\.?|EXT\.?|EST\.?|I\/E\.?)[\s.]+/i, "").trim() || heading;
}

export function TimelinePanel({
  scriptId, elements, open, fullscreen, canEdit,
  onClose, onToggleFullscreen, onExitFullscreen, onReorderScript, onJumpToScene, onReadLanes,
  initialCardW = 200, initialCardH = 170, onPersistCardSize,
}) {
  const [data, setData] = useState({ lanes: [], scenes: [] });
  const [mode, setMode] = useState("script");
  const [readSel, setReadSel] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [cardW, setCardW] = useState(initialCardW);
  const [cardH, setCardH] = useState(initialCardH);
  const [dropIdx, setDropIdx] = useState(null);      // script-order ghost slot
  const [ghost, setGhost] = useState(null);          // story-order ghost { laneId, col }
  const [past, setPast] = useState([]);              // story-map undo stack
  const [future, setFuture] = useState([]);          // story-map redo stack
  const [laneDrop, setLaneDrop] = useState(null);    // lane insertion index while dragging a lane
  const dragId = useRef(null);
  const laneDragId = useRef(null);
  const laneDropRef = useRef(null);
  const dataRef = useRef(data);
  const scrollRef = useRef(null);
  const persistRef = useRef(null);
  const confirm = useConfirm();
  dataRef.current = data;
  laneDropRef.current = laneDrop;

  useEffect(() => { setCardW(initialCardW); setCardH(initialCardH); }, [initialCardW, initialCardH]);

  const colStep = cardW + GAP;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/scripts/${scriptId}/timeline`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load the timeline.");
    } finally { setLoading(false); }
  }, [scriptId]);

  const sceneSig = useMemo(
    () => (elements || []).filter((e) => e.type === "scene_heading").map((e) => `${e.id}:${e.text}`).join("|"),
    [elements]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (open) { const t = setTimeout(load, 600); return () => clearTimeout(t); } }, [sceneSig, open, load]);

  // dock height grows with card height so cards stay visible; 0 when full-screen/closed
  const dockH = Math.min(460, Math.max(244, cardH + 150));
  useEffect(() => {
    const root = document.documentElement;
    if (!open || fullscreen) { root.style.setProperty("--tl-dock-h", "0px"); return; }
    root.style.setProperty("--tl-dock-h", dockH + "px");
    return () => root.style.setProperty("--tl-dock-h", "0px");
  }, [open, fullscreen, dockH]);

  const onSize = useCallback((w, h) => {
    setCardW(w); setCardH(h);
    if (persistRef.current) clearTimeout(persistRef.current);
    persistRef.current = setTimeout(() => onPersistCardSize?.(w, h), 500);
  }, [onPersistCardSize]);

  const lanesById = useMemo(() => Object.fromEntries((data.lanes || []).map((l) => [l.id, l])), [data.lanes]);
  const scriptScenes = useMemo(() => [...(data.scenes || [])].sort((a, b) => a.number - b.number), [data.scenes]);
  const storyScenes = useMemo(() => data.scenes || [], [data.scenes]);
  const maxCol = useMemo(
    () => Math.max(0, storyScenes.length - 1, ...storyScenes.map((s) => Math.round(s.story_order || 0))),
    [storyScenes]);
  const trackWidth = (maxCol + 2) * colStep;

  const clearDrag = useCallback(() => { dragId.current = null; setDropIdx(null); setGhost(null); }, []);

  // ---------- story-map history (undo/redo) ----------
  const snap = useCallback(() => ({
    lanes: (dataRef.current.lanes || []).map((l) => ({ ...l })),
    scenes: (dataRef.current.scenes || []).map((s) => ({
      scene_id: s.scene_id, lane_id: s.lane_id, story_order: s.story_order, label: s.label || "",
    })),
  }), []);

  const pushHistory = useCallback(() => {
    const s = snap();
    setPast((p) => [...p.slice(-49), s]);
    setFuture([]);
  }, [snap]);

  const applySnap = useCallback((s) => {
    const metaById = Object.fromEntries(s.scenes.map((x) => [x.scene_id, x]));
    setData((d) => ({
      ...d,
      lanes: s.lanes,
      scenes: (d.scenes || []).map((sc) => {
        const m = metaById[sc.scene_id];
        return m ? { ...sc, lane_id: m.lane_id, story_order: m.story_order, label: m.label } : sc;
      }),
    }));
    api.put(`/scripts/${scriptId}/timeline/restore`, s).catch(() => toast.error("Couldn't save that change."));
  }, [scriptId]);

  const undo = useCallback(() => {
    if (!past.length) return;
    const prev = past[past.length - 1];
    setFuture((f) => [...f, snap()]);
    setPast(past.slice(0, -1));
    applySnap(prev);
  }, [past, snap, applySnap]);

  const redo = useCallback(() => {
    if (!future.length) return;
    const next = future[future.length - 1];
    setPast((p) => [...p.slice(-49), snap()]);
    setFuture(future.slice(0, -1));
    applySnap(next);
  }, [future, snap, applySnap]);

  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;

  useEffect(() => {
    if (!open || !canEdit || mode !== "story") return;
    const fn = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k !== "z" && k !== "y") return;
      const t = e.target;
      if (t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT" || t.isContentEditable)) return;
      e.preventDefault();
      if (k === "y" || (k === "z" && e.shiftKey)) redoRef.current();
      else undoRef.current();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open, canEdit, mode]);

  const handleJump = useCallback((sceneId) => {
    if (fullscreen) { onExitFullscreen?.(); setTimeout(() => onJumpToScene?.(sceneId), 90); }
    else onJumpToScene?.(sceneId);
  }, [fullscreen, onExitFullscreen, onJumpToScene]);

  // ---------- script-order reorder (moves the real script) ----------
  const moveScript = useCallback((sceneId, dir) => {
    const { preamble, blocks } = splitBlocks(elements);
    const i = blocks.findIndex((b) => b.sceneId === sceneId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= blocks.length) return;
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    onReorderScript?.(flatten(preamble, blocks));
  }, [elements, onReorderScript]);

  const dropScriptAt = useCallback((index) => {
    const sid = dragId.current;
    if (!sid || index == null) return;
    const { preamble, blocks } = splitBlocks(elements);
    const from = blocks.findIndex((b) => b.sceneId === sid);
    if (from < 0) return;
    const [moved] = blocks.splice(from, 1);
    let to = index;
    if (from < index) to = index - 1;
    to = Math.max(0, Math.min(to, blocks.length));
    blocks.splice(to, 0, moved);
    onReorderScript?.(flatten(preamble, blocks));
  }, [elements, onReorderScript]);

  // ---------- story-order (timeline map only) ----------
  const place = useCallback((sceneId, laneId, col) => {
    pushHistory();
    const used = new Set(storyScenes.filter((s) => s.lane_id === laneId && s.scene_id !== sceneId).map((s) => Math.round(s.story_order || 0)));
    let c = Math.max(0, Math.round(col));
    while (used.has(c)) c += 1; // auto-nudge to the next free column in this lane
    setData((d) => ({ ...d, scenes: d.scenes.map((s) => (s.scene_id === sceneId ? { ...s, lane_id: laneId, story_order: c } : s)) }));
    api.put(`/scripts/${scriptId}/timeline/scene/${sceneId}`, { lane_id: laneId, story_order: c })
      .catch(() => toast.error("Couldn't move that scene."));
  }, [storyScenes, scriptId, pushHistory]);

  const nudgeStory = useCallback((sceneId, dir) => {
    const s = storyScenes.find((x) => x.scene_id === sceneId);
    if (!s) return;
    const cur = Math.round(s.story_order || 0);
    const target = cur + dir;
    if (target < 0) return;
    pushHistory();
    const occ = storyScenes.find((x) => x.lane_id === s.lane_id && x.scene_id !== sceneId && Math.round(x.story_order || 0) === target);
    const items = [{ scene_id: sceneId, story_order: target }];
    if (occ) items.push({ scene_id: occ.scene_id, story_order: cur });
    setData((d) => ({
      ...d,
      scenes: d.scenes.map((x) => {
        if (x.scene_id === sceneId) return { ...x, story_order: target };
        if (occ && x.scene_id === occ.scene_id) return { ...x, story_order: cur };
        return x;
      }),
    }));
    api.put(`/scripts/${scriptId}/timeline/story-order`, { items }).catch(() => toast.error("Couldn't save the order."));
  }, [storyScenes, scriptId, pushHistory]);

  const closeGap = useCallback((laneId, start, shift) => {
    const items = storyScenes
      .filter((s) => s.lane_id === laneId && Math.round(s.story_order || 0) >= start)
      .map((s) => ({ scene_id: s.scene_id, story_order: Math.round(s.story_order || 0) - shift }));
    if (!items.length) return;
    pushHistory();
    const byId = Object.fromEntries(items.map((x) => [x.scene_id, x.story_order]));
    setData((d) => ({ ...d, scenes: d.scenes.map((x) => (byId[x.scene_id] != null ? { ...x, story_order: byId[x.scene_id] } : x)) }));
    api.put(`/scripts/${scriptId}/timeline/story-order`, { items }).catch(() => toast.error("Couldn't close the gap."));
  }, [storyScenes, scriptId, pushHistory]);

  const addGap = useCallback((laneId, at) => {
    const items = storyScenes
      .filter((s) => s.lane_id === laneId && Math.round(s.story_order || 0) >= at)
      .map((s) => ({ scene_id: s.scene_id, story_order: Math.round(s.story_order || 0) + 1 }));
    if (!items.length) return;
    pushHistory();
    const byId = Object.fromEntries(items.map((x) => [x.scene_id, x.story_order]));
    setData((d) => ({ ...d, scenes: d.scenes.map((x) => (byId[x.scene_id] != null ? { ...x, story_order: byId[x.scene_id] } : x)) }));
    api.put(`/scripts/${scriptId}/timeline/story-order`, { items }).catch(() => toast.error("Couldn't add the gap."));
  }, [storyScenes, scriptId, pushHistory]);

  const changeLane = useCallback((sceneId, laneId) => {
    const s = storyScenes.find((x) => x.scene_id === sceneId);
    place(sceneId, laneId, s ? Math.round(s.story_order || 0) : 0);
  }, [storyScenes, place]);

  const setLabel = useCallback((sceneId, label) => {
    pushHistory();
    setData((d) => ({ ...d, scenes: d.scenes.map((x) => (x.scene_id === sceneId ? { ...x, label } : x)) }));
    api.put(`/scripts/${scriptId}/timeline/scene/${sceneId}`, { label }).catch(() => toast.error("Couldn't save the label."));
  }, [scriptId, pushHistory]);

  const handleStoryDragOver = useCallback((e, laneId) => {
    if (!canEdit || !dragId.current) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - 12;
    const col = Math.max(0, Math.round(x / colStep));
    setGhost((g) => (g && g.laneId === laneId && g.col === col ? g : { laneId, col }));
  }, [canEdit, colStep]);

  const dropStory = useCallback((laneId) => {
    const sid = dragId.current;
    const col = ghost?.col ?? 0;
    clearDrag();
    if (sid) place(sid, laneId, col);
  }, [ghost, place, clearDrag]);

  // ---------- lanes ----------
  const saveLanes = useCallback(async (lanes) => {
    pushHistory();
    const valid = new Set(lanes.map((l) => l.id));
    const fallback = lanes[0]?.id;
    setData((d) => ({
      ...d,
      lanes,
      scenes: (d.scenes || []).map((s) => (valid.has(s.lane_id) ? s : { ...s, lane_id: fallback })),
    }));
    try { await api.put(`/scripts/${scriptId}/timeline/lanes`, { lanes }); }
    catch { toast.error("Couldn't save lanes."); }
  }, [scriptId, pushHistory]);
  const addLane = () => saveLanes([...(data.lanes || []), { id: genId(), name: "New lane", color: LANE_PALETTE[(data.lanes?.length || 0) % LANE_PALETTE.length] }]);
  const renameLane = (id, name) => saveLanes(data.lanes.map((l) => (l.id === id ? { ...l, name } : l)));
  const cycleColor = (id) => saveLanes(data.lanes.map((l) => (l.id === id ? { ...l, color: LANE_PALETTE[(LANE_PALETTE.indexOf(l.color) + 1) % LANE_PALETTE.length] } : l)));
  const removeLane = async (id) => {
    if ((data.lanes || []).length <= 1) return;
    const lane = data.lanes.find((l) => l.id === id);
    const target = data.lanes.find((l) => l.id !== id);
    const n = storyScenes.filter((s) => s.lane_id === id).length;
    const ok = await confirm({
      title: `Remove the "${lane?.name || "lane"}" lane?`,
      description: n > 0
        ? `${n} scene${n > 1 ? "s" : ""} in this lane will move to "${target?.name}". Your script itself is never touched.`
        : "This lane is empty — nothing else changes. Your script itself is never touched.",
      confirmLabel: "Remove lane",
      tone: "danger",
    });
    if (!ok) return;
    saveLanes(data.lanes.filter((l) => l.id !== id));
  };
  const reorderLanes = (fromId, toIdx) => {
    const lanes = [...(data.lanes || [])];
    const from = lanes.findIndex((l) => l.id === fromId);
    if (from < 0 || toIdx == null) return;
    let to = toIdx;
    if (from < toIdx) to -= 1;
    to = Math.max(0, Math.min(to, lanes.length - 1));
    if (to === from) return;
    const [moved] = lanes.splice(from, 1);
    lanes.splice(to, 0, moved);
    saveLanes(lanes);
  };

  const toggleReadSel = (id) => setReadSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (!open) return null;

  const wrapClass = fullscreen
    ? "fixed inset-0 z-50 bg-[var(--paper)] flex flex-col"
    : "fixed left-0 right-0 z-40 bg-[var(--paper)] border-t border-[var(--line)] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] flex flex-col";
  const wrapStyle = fullscreen ? {} : { bottom: "var(--tr-dock-h, 0px)", height: dockH };
  const scenes = data.scenes || [];

  // ---------- render ----------
  return (
    <div className={wrapClass} style={wrapStyle} data-testid="timeline-panel">
      {/* header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--line)] shrink-0 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex items-center gap-2 font-display text-lg text-[var(--ink-primary)] shrink-0"><Rows3 size={16} className="text-[var(--brand)]" /> Timeline</span>
          <div className="flex rounded-md border border-[var(--line)] overflow-hidden text-xs shrink-0">
            <button onClick={() => setMode("script")} className={`px-3 py-1.5 ${mode === "script" ? "bg-[var(--brand)] text-white" : "text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"}`} data-testid="timeline-mode-script">Script order</button>
            <button onClick={() => setMode("story")} className={`px-3 py-1.5 ${mode === "story" ? "bg-[var(--brand)] text-white" : "text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"}`} data-testid="timeline-mode-story">Story order</button>
          </div>
          {mode === "story" && canEdit && (
            <button onClick={addLane} className="text-xs text-[var(--brand)] hover:underline inline-flex items-center gap-1 shrink-0" data-testid="timeline-add-lane"><Plus size={13} /> Lane</button>
          )}
          {mode === "story" && onReadLanes && (
            <button
              onClick={() => readSel.size > 0 && onReadLanes([...readSel])}
              disabled={readSel.size === 0}
              className={`text-xs inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-md border transition-colors ${readSel.size > 0 ? "border-[var(--brand)] text-[var(--brand)] hover:bg-[var(--brand-soft)]" : "border-[var(--line)] text-[var(--ink-muted)] cursor-not-allowed"}`}
              title={readSel.size > 0 ? "Read only the checked lanes' scenes" : "Check one or more lanes below to read just those storylines"}
              data-testid="timeline-read-lanes-btn"
            >
              <BookOpen size={13} /> Read {readSel.size > 0 ? `${readSel.size} lane${readSel.size > 1 ? "s" : ""}` : "lanes"}
            </button>
          )}
          {mode === "story" && canEdit && (
            <div className="flex items-center rounded-md border border-[var(--line)] overflow-hidden shrink-0">
              <button onClick={undo} disabled={!past.length} className="p-1 hover:bg-[var(--warm-accent)] disabled:opacity-30 text-[var(--ink-secondary)]" title="Undo (⌘Z)" data-testid="timeline-undo-btn"><Undo2 size={13} /></button>
              <button onClick={redo} disabled={!future.length} className="p-1 border-l border-[var(--line)] hover:bg-[var(--warm-accent)] disabled:opacity-30 text-[var(--ink-secondary)]" title="Redo (⌘⇧Z)" data-testid="timeline-redo-btn"><Redo2 size={13} /></button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* card size sliders */}
          <div className="hidden sm:flex items-center gap-2 text-[var(--ink-muted)]" title="Card size">
            <MoveHorizontal size={13} />
            <input type="range" min={W_MIN} max={W_MAX} value={cardW} onChange={(e) => onSize(Number(e.target.value), cardH)}
              className="w-16 accent-[var(--brand)] cursor-pointer" data-testid="timeline-card-width" title="Card width" />
            <MoveVertical size={13} />
            <input type="range" min={H_MIN} max={H_MAX} value={cardH} onChange={(e) => onSize(cardW, Number(e.target.value))}
              className="w-16 accent-[var(--brand)] cursor-pointer" data-testid="timeline-card-height" title="Card height" />
          </div>
          <button onClick={onToggleFullscreen} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="timeline-fullscreen" title={fullscreen ? "Exit full screen" : "Full screen"}>
            {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="timeline-close" title="Close"><X size={16} /></button>
        </div>
      </div>

      {/* hint */}
      <div className="px-4 py-1.5 text-[11px] text-[var(--ink-muted)] border-b border-[var(--line)] shrink-0">
        {mode === "script"
          ? "Drag or use ◀ ▶ to reorder scenes in your script. Click a card to jump to that scene."
          : "Chronological view — drag cards left/right to set the time, drop on a lane to assign it. Hover a blank stretch to close the gap, drag a lane's grip to reorder rows. This never changes your script."}
      </div>

      {/* body */}
      <div ref={scrollRef} className="flex-1 overflow-auto" data-testid="timeline-body">
        {loading && scenes.length === 0 ? (
          <p className="p-6 text-sm text-[var(--ink-muted)]">Loading…</p>
        ) : scenes.length === 0 ? (
          <p className="p-6 text-sm text-[var(--ink-muted)] italic" data-testid="timeline-empty">No scenes yet — add a scene heading to your script and it'll appear here.</p>
        ) : mode === "script" ? (
          <div
            className="flex items-start gap-3.5 p-4 min-w-min"
            data-testid="timeline-script-track"
            onDragOver={(e) => {
              if (!canEdit || !dragId.current) return;
              // Always allow dropping anywhere on the track (incl. the ghost
              // placeholder and the gaps between cards) — without preventDefault
              // here the browser cancels the drop.
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              const cards = e.currentTarget.querySelectorAll(':scope > [data-testid^="timeline-card-"]');
              const last = cards[cards.length - 1];
              if (last && e.clientX > last.getBoundingClientRect().right) {
                // Past the right edge of the last card → drop at the very end,
                // overriding any stale index left from hovering the last card.
                if (dropIdx !== scriptScenes.length) setDropIdx(scriptScenes.length);
              } else if (dropIdx == null) {
                setDropIdx(scriptScenes.length);
              }
            }}
            onDrop={(e) => { e.preventDefault(); dropScriptAt(dropIdx); clearDrag(); }}
          >
            {scriptScenes.flatMap((s, i) => {
              const nodes = [];
              if (dropIdx === i) nodes.push(<Ghost key={`g-${i}`} w={cardW} h={cardH} />);
              nodes.push(
                <SceneCard
                  key={s.scene_id} s={s} lane={lanesById[s.lane_id]} mode="script" canEdit={canEdit} cardW={cardW} cardH={cardH}
                  onJump={() => handleJump(s.scene_id)}
                  onPrev={() => moveScript(s.scene_id, -1)} onNext={() => moveScript(s.scene_id, 1)}
                  onDragStart={(e) => {
                    try { e.dataTransfer.setData("text/plain", s.scene_id); e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
                    dragId.current = s.scene_id;
                  }}
                  onDragEnd={clearDrag}
                  onCardDragOver={(e) => {
                    if (!canEdit || !dragId.current) return;
                    e.preventDefault(); e.stopPropagation();
                    e.dataTransfer.dropEffect = "move";
                    const rect = e.currentTarget.getBoundingClientRect();
                    const left = (e.clientX - rect.left) < rect.width / 2;
                    setDropIdx(left ? i : i + 1);
                  }}
                />
              );
              return nodes;
            })}
            {dropIdx === scriptScenes.length && <Ghost key="g-end" w={cardW} h={cardH} />}
          </div>
        ) : (
          <div className="min-w-min">
            {(data.lanes || []).map((lane, laneIdx) => {
              const laneScenes = storyScenes.filter((s) => s.lane_id === lane.id);
              const cols = laneScenes.map((s) => Math.round(s.story_order || 0)).sort((a, b) => a - b);
              const gaps = [];
              const joints = []; // boundaries between two directly adjacent cards
              if (cols.length) {
                if (cols[0] > 0) gaps.push({ start: 0, count: cols[0] });
                for (let k = 1; k < cols.length; k++) {
                  const span = cols[k] - cols[k - 1] - 1;
                  if (span > 0) gaps.push({ start: cols[k - 1] + 1, count: span });
                  else joints.push(cols[k]);
                }
              }
              return (
                <div
                  key={lane.id}
                  className="relative flex items-stretch border-b border-[var(--line)]"
                  onDragOver={(e) => {
                    if (!laneDragId.current) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    const r = e.currentTarget.getBoundingClientRect();
                    const idx = e.clientY < r.top + r.height / 2 ? laneIdx : laneIdx + 1;
                    if (laneDropRef.current !== idx) setLaneDrop(idx);
                  }}
                  onDrop={(e) => {
                    if (!laneDragId.current) return;
                    e.preventDefault();
                    const fromId = laneDragId.current;
                    laneDragId.current = null;
                    const idx = laneDropRef.current;
                    setLaneDrop(null);
                    reorderLanes(fromId, idx);
                  }}
                  data-testid={`timeline-lane-row-${lane.id}`}
                >
                  {laneDrop === laneIdx && <div className="absolute top-0 inset-x-0 h-[3px] bg-[var(--brand)] z-30 pointer-events-none" data-testid="timeline-lane-drop-indicator" />}
                  {laneIdx === (data.lanes || []).length - 1 && laneDrop === (data.lanes || []).length && <div className="absolute bottom-0 inset-x-0 h-[3px] bg-[var(--brand)] z-30 pointer-events-none" data-testid="timeline-lane-drop-indicator" />}
                  {/* lane gutter */}
                  <div className="sticky left-0 z-10 w-40 shrink-0 bg-[var(--canvas)] border-r border-[var(--line)] p-2 flex flex-col gap-1.5">
                    <div className="flex items-center gap-1.5">
                      {canEdit && (data.lanes || []).length > 1 && (
                        <span
                          draggable
                          onDragStart={(e) => {
                            try { e.dataTransfer.setData("text/plain", lane.id); e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
                            laneDragId.current = lane.id;
                          }}
                          onDragEnd={() => { laneDragId.current = null; setLaneDrop(null); }}
                          className="cursor-grab text-[var(--ink-muted)] hover:text-[var(--ink-secondary)] shrink-0 -ml-1"
                          title="Drag to reorder lanes"
                          data-testid={`timeline-lane-grip-${lane.id}`}
                        >
                          <GripVertical size={12} />
                        </span>
                      )}
                      {onReadLanes && (
                        <input
                          type="checkbox" checked={readSel.has(lane.id)} onChange={() => toggleReadSel(lane.id)}
                          className="accent-[var(--brand)] cursor-pointer shrink-0 w-3 h-3"
                          title="Include this lane in a filtered read"
                          data-testid={`timeline-read-check-${lane.id}`}
                        />
                      )}
                      <button onClick={() => canEdit && cycleColor(lane.id)} className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: lane.color }} title="Change colour" data-testid={`timeline-lane-color-${lane.id}`} />
                      <input
                        defaultValue={lane.name} disabled={!canEdit} key={`${lane.id}-${lane.name}`}
                        onBlur={(e) => e.target.value !== lane.name && renameLane(lane.id, e.target.value)}
                        className="bg-transparent text-xs font-semibold text-[var(--ink-primary)] outline-none w-full truncate"
                        data-testid={`timeline-lane-name-${lane.id}`}
                      />
                    </div>
                    {canEdit && (data.lanes || []).length > 1 && (
                      <button onClick={() => removeLane(lane.id)} className="text-[10px] text-[var(--ink-muted)] hover:text-[var(--err)] inline-flex items-center gap-1 self-start" data-testid={`timeline-lane-delete-${lane.id}`}><Trash2 size={11} /> Remove</button>
                    )}
                  </div>
                  {/* lane track — height fits one row of cards */}
                  <div
                    className="relative flex-1"
                    style={{ height: cardH + 24, minWidth: trackWidth }}
                    onDragOver={(e) => handleStoryDragOver(e, lane.id)}
                    onDrop={(e) => { e.preventDefault(); dropStory(lane.id); }}
                    data-testid={`timeline-lane-track-${lane.id}`}
                  >
                    {/* column guides */}
                    {Array.from({ length: maxCol + 2 }).map((_, c) => (
                      <div key={c} className="absolute top-0 bottom-0 w-px bg-[var(--line)] opacity-40" style={{ left: c * colStep + 12 }} />
                    ))}
                    {/* blank stretches — hover to close the gap */}
                    {canEdit && gaps.map((g) => (
                      <div
                        key={`gap-${g.start}`}
                        className="absolute top-2 bottom-2 z-10 group/gap rounded"
                        style={{ left: g.start * colStep + 12, width: Math.max(40, g.count * colStep - GAP) }}
                        data-testid={`timeline-gap-${lane.id}-${g.start}`}
                      >
                        <div className="absolute inset-0 rounded border border-dashed border-transparent group-hover/gap:border-[var(--line)] group-hover/gap:bg-[var(--warm-accent)]/40 transition-colors pointer-events-none" />
                        <div className="absolute inset-0 hidden group-hover/gap:flex flex-col items-center justify-center gap-1">
                          <button
                            onClick={() => closeGap(lane.id, g.start, g.count)}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand)] text-white shadow hover:opacity-90 whitespace-nowrap"
                            title={`Pull the later scenes ${g.count} column${g.count > 1 ? "s" : ""} to the left`}
                            data-testid={`timeline-gap-close-${lane.id}-${g.start}`}
                          >
                            Close gap{g.count > 1 ? ` (${g.count})` : ""}
                          </button>
                          {g.count > 1 && (
                            <button
                              onClick={() => closeGap(lane.id, g.start, 1)}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--paper)] border border-[var(--line)] text-[var(--ink-secondary)] shadow hover:border-[var(--brand)]/60 whitespace-nowrap"
                              title="Remove just one blank column"
                              data-testid={`timeline-gap-one-${lane.id}-${g.start}`}
                            >
                              − 1 column
                            </button>
                          )}
                          <button
                            onClick={() => addGap(lane.id, g.start)}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--paper)] border border-[var(--line)] text-[var(--ink-secondary)] shadow hover:border-[var(--brand)]/60 whitespace-nowrap"
                            title="Push the later scenes one column right to widen this gap"
                            data-testid={`timeline-gap-widen-${lane.id}-${g.start}`}
                          >
                            + 1 column
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* seams between adjacent cards — hover to push a gap open */}
                    {canEdit && joints.map((c) => (
                      <div
                        key={`joint-${c}`}
                        className="absolute top-2 bottom-2 z-10 group/joint"
                        style={{ left: c * colStep + 12 - GAP, width: GAP }}
                        data-testid={`timeline-joint-${lane.id}-${c}`}
                      >
                        <div className="absolute inset-y-0 left-1/2 w-px border-l border-dashed border-transparent group-hover/joint:border-[var(--brand)] transition-colors pointer-events-none" />
                        <button
                          onClick={() => addGap(lane.id, c)}
                          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden group-hover/joint:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand)] text-white shadow whitespace-nowrap"
                          title="Push the later scenes one column right to open a gap here"
                          data-testid={`timeline-addgap-${lane.id}-${c}`}
                        >
                          <Plus size={10} /> Add gap
                        </button>
                      </div>
                    ))}
                    {/* drop ghost */}
                    {ghost && ghost.laneId === lane.id && <Ghost absolute left={ghost.col * colStep + 12} w={cardW} h={cardH} />}
                    {laneScenes.map((s) => (
                      <div key={s.scene_id} className="absolute" style={{ left: Math.round(s.story_order || 0) * colStep + 12, top: 12 }}>
                        <SceneCard
                          s={s} lane={lane} mode="story" canEdit={canEdit} cardW={cardW} cardH={cardH}
                          onJump={() => handleJump(s.scene_id)}
                          onPrev={() => nudgeStory(s.scene_id, -1)} onNext={() => nudgeStory(s.scene_id, 1)}
                          lanes={data.lanes} onLane={(lid) => changeLane(s.scene_id, lid)}
                          onLabel={(v) => setLabel(s.scene_id, v)}
                          onDragStart={(e) => {
                            try { e.dataTransfer.setData("text/plain", s.scene_id); e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
                            dragId.current = s.scene_id;
                          }}
                          onDragEnd={clearDrag}
                        />
                      </div>
                    ))}
                    {laneScenes.length === 0 && !ghost && <p className="absolute top-1/2 -translate-y-1/2 left-3 text-[11px] text-[var(--ink-muted)] italic">Drop scenes here.</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Ghost({ w, h, absolute, left }) {
  const style = absolute ? { left, top: 12, width: w, height: h, position: "absolute" } : { width: w, height: h };
  return <div style={style} className="shrink-0 rounded-md border-2 border-dashed border-[var(--brand)] bg-[var(--brand)]/10 pointer-events-none" data-testid="timeline-drop-ghost" />;
}

function SceneCard({ s, lane, mode, canEdit, cardW, cardH, onJump, onPrev, onNext, lanes, onLane, onLabel, onDragStart, onDragEnd, onCardDragOver }) {
  const chars = s.characters || [];

  // smart content tiers + font scaling (heading floors at 9px per spec)
  const scale = Math.max(0.6, Math.min(1, cardH / 180));
  const headPx = Math.max(9, Math.round(13 * scale));
  const metaPx = Math.max(8, Math.round(10 * scale));
  const bodyPx = Math.max(9, Math.round(11 * scale));
  const tagPx = Math.max(8, Math.round(9 * scale));
  const chevSz = Math.max(11, Math.round(14 * scale));

  const showMeta = cardH >= 88;
  const showSynopsis = cardH >= 132;
  const showTags = cardH >= 106;
  const showStory = mode === "story" && cardH >= 150;
  const tinyHeading = cardW < 150;
  const headingText = tinyHeading ? compactHeading(s.heading) : s.heading;
  const maxTags = cardW < 130 ? 2 : 3;

  return (
    <div
      draggable={canEdit}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onCardDragOver}
      className="shrink-0 rounded-md border border-[var(--line)] bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col"
      style={{ width: cardW, height: cardH }}
      data-testid={`timeline-card-${s.scene_id}`}
    >
      <div className="shrink-0" style={{ height: 4, background: s.color || lane?.color || "var(--brand)" }} />
      <div className="px-2.5 py-2 flex-1 min-h-0 flex flex-col gap-1 overflow-hidden">
        <div className="flex items-start justify-between gap-1.5">
          <button onClick={onJump} className="text-left min-w-0 group flex-1" data-testid={`timeline-card-jump-${s.scene_id}`}>
            {showMeta && (
              <span className="block uppercase tracking-widest text-[var(--ink-muted)]" style={{ fontSize: metaPx }}>
                Scene {s.number}{cardW >= 130 ? ` · ~${s.pages} pg` : ""}
              </span>
            )}
            <p className="font-semibold text-[var(--ink-primary)] group-hover:text-[var(--brand)] leading-tight" style={{ fontSize: headPx, display: "-webkit-box", WebkitLineClamp: cardH < 110 ? 1 : 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {headingText || "(untitled scene)"}
            </p>
          </button>
          {canEdit && (
            <div className="flex shrink-0">
              <button onClick={onPrev} className="p-0.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)]" title="Move earlier" data-testid={`timeline-card-prev-${s.scene_id}`}><ChevronLeft size={chevSz} /></button>
              <button onClick={onNext} className="p-0.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)]" title="Move later" data-testid={`timeline-card-next-${s.scene_id}`}><ChevronRight size={chevSz} /></button>
            </div>
          )}
        </div>

        {showSynopsis && (
          s.synopsis ? (
            <p className="text-[var(--ink-secondary)] leading-snug" style={{ fontSize: bodyPx, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.synopsis}</p>
          ) : (
            <p className="text-[var(--ink-muted)] italic" style={{ fontSize: bodyPx }}>No synopsis yet.</p>
          )
        )}

        {showTags && chars.length > 0 && (
          <div className="flex flex-wrap gap-1 overflow-hidden" style={{ maxHeight: tagPx * 2.6 }}>
            {chars.slice(0, maxTags).map((c) => <span key={c} className="uppercase tracking-wider bg-[var(--warm-accent)] text-[var(--ink-secondary)] px-1.5 py-0.5 rounded" style={{ fontSize: tagPx }}>{c}</span>)}
            {chars.length > maxTags && <span className="text-[var(--ink-muted)]" style={{ fontSize: tagPx }}>+{chars.length - maxTags}</span>}
          </div>
        )}

        {showStory && (
          <div className="space-y-1.5 pt-1.5 mt-auto border-t border-[var(--line)]">
            <input
              defaultValue={s.label} disabled={!canEdit} key={`${s.scene_id}-${s.label || ""}`}
              placeholder="When? e.g. Flashback – 1985"
              onBlur={(e) => e.target.value !== (s.label || "") && onLabel?.(e.target.value)}
              className="w-full bg-transparent text-[var(--ink-primary)] border border-[var(--line)] rounded px-1.5 py-1 outline-none focus:border-[var(--brand)]"
              style={{ fontSize: bodyPx }}
              data-testid={`timeline-card-label-${s.scene_id}`}
            />
            {canEdit && (
              <select
                value={s.lane_id} onChange={(e) => onLane?.(e.target.value)}
                className="w-full bg-white border border-[var(--line)] rounded px-1.5 py-1 outline-none"
                style={{ fontSize: bodyPx }}
                data-testid={`timeline-card-lane-${s.scene_id}`}
              >
                {(lanes || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
